import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { EquipmentFlagGate } from "@/components/equipment/EquipmentFlagGate";
import { EnrichmentDialog } from "@/components/equipment/EnrichmentDialog";
import { ProductDataTable } from "@/components/equipment/ProductDataTable";
import { useEquipmentAccess } from "@/hooks/use-equipment";
import { EnrichmentError, useFirecrawlCredits, useStartEnrichment } from "@/hooks/use-equipment-enrichment";
import { useProductOverview, type ProductOverviewRow } from "@/hooks/use-equipment-products";
import { runLimited } from "@/lib/equipment/enrichment";

/** «Hent for alle» kjører én modell per kall, høyst to samtidig. */
const BATCH_CONCURRENCY = 2;

/**
 * /utstyr/produktdata (VIB-2918, skjerm K). Modeller med status, «Hent for alle»
 * for modeller uten data, og køen av forslag som venter på gjennomsyn.
 * Bare for admin og utstyrsansvarlige.
 */
export default function EquipmentProductDataPage() {
  return (
    <EquipmentFlagGate>
      <EquipmentProductData />
    </EquipmentFlagGate>
  );
}

function EquipmentProductData() {
  const access = useEquipmentAccess();
  const { data: overview, isLoading } = useProductOverview(access.canManage);
  const credits = useFirecrawlCredits(access.canManage);
  const start = useStartEnrichment();
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [batch, setBatch] = useState<{ done: number; total: number } | null>(null);
  const stopRef = useRef(false);
  const [review, setReview] = useState<ProductOverviewRow | null>(null);

  if (access.isLoading || (access.canManage && isLoading)) {
    return (
      <div className="page-container flex justify-center py-12">
        <SrIcon name="loader2" className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!access.canManage || !overview) {
    return (
      <div className="page-container animate-fade-in space-y-2">
        <h1 className="page-title">Produktdata</h1>
        <p className="text-muted-foreground">Produktdata er for administratorer og utstyrsansvarlige.</p>
      </div>
    );
  }

  const missing = overview.products.filter((p) => p.status === "missing" || p.status === "failed");
  const remaining = credits.data;

  const fetchOne = async (p: ProductOverviewRow) => {
    setBusy((s) => new Set(s).add(p.id));
    try {
      await start.mutateAsync(p.id);
    } finally {
      setBusy((s) => {
        const next = new Set(s);
        next.delete(p.id);
        return next;
      });
    }
  };

  const fetchSingle = (p: ProductOverviewRow) =>
    fetchOne(p)
      .then(() => toast.success(`Forslag klart for ${p.brand_name} ${p.model}`))
      .catch((e: Error) => toast.error(e.message))
      .finally(() => credits.refetch());

  const fetchAll = async () => {
    stopRef.current = false;
    setBatch({ done: 0, total: missing.length });
    let failed = 0;
    await runLimited(
      missing,
      BATCH_CONCURRENCY,
      async (p) => {
        try {
          await fetchOne(p);
        } catch (e) {
          failed++;
          // Tom for credits: stopp, og behold det som er hentet.
          if (e instanceof EnrichmentError && e.outOfCredits) {
            stopRef.current = true;
            toast.error("Firecrawl er tom for credits. Hentingen er stoppet.");
          }
        }
        setBatch((b) => (b ? { ...b, done: b.done + 1 } : b));
      },
      () => stopRef.current,
    );
    setBatch(null);
    credits.refetch();
    toast.success(`Hentingen er ferdig${failed ? `, ${failed} feilet` : ""}. Gå gjennom forslagene i lista.`);
  };

  return (
    <div className="page-container animate-fade-in space-y-6">
      <nav aria-label="Brødsmuler" className="flex gap-1.5 text-sm text-muted-foreground">
        <Link to="/utstyr" className="hover:underline">Utstyr</Link>
        <span>/</span>
        <span>Produktdata</span>
      </nav>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] flex items-center gap-2">
          <SrIcon name="database" className="h-6 w-6" />
          Produktdata
        </h1>
        {batch ? (
          <Button variant="outline" onClick={() => (stopRef.current = true)}>
            Stopp ({batch.done} av {batch.total})
          </Button>
        ) : (
          <Button disabled={missing.length === 0 || busy.size > 0 || remaining === 0} onClick={fetchAll}>
            <SrIcon name="download" className="h-4 w-4" />
            Hent for alle uten data ({missing.length})
          </Button>
        )}
      </div>
      <Card>
        <CardContent className="pt-4">
          <ProductDataTable overview={overview} busyIds={busy} onFetch={fetchSingle} onReview={setReview} disabled={!!batch} />
          {overview.withoutProduct > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              {overview.withoutProduct} rader har ingen modell ennå. Koble dem med «Hent produktdata» på utstyrskortet.
            </p>
          )}
        </CardContent>
      </Card>
      <EnrichmentDialog open={!!review} onOpenChange={(o) => !o && setReview(null)} product={review} />
    </div>
  );
}
